//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Handlebars from 'handlebars'
import {
  ERC20Wei,
  aprToDisplay,
  erc20WeiToCommaDecString,
  ethValueDisplay,
  toLongUTCDateString,
  toUTCTimeString,
} from './conversions'
import { TemplateDataObject } from './TemplateDataObject'

export enum EventTypeForTemplate {
  Undefined = 'Undefined',
  PoolCreated = 'PoolCreated',
  LoanCreated = 'LoanCreated',
  AccountCredit = 'AccountCredit',
  AccountDebit = 'AccountDebit',
  Deposit = 'Deposit', // TERM DEPOSIT
  PoolDeposit = 'PoolDeposit', // FLEX and Dynamic DEPOSIT
  //  DepositTransfer = 'Deposit Transfer',
  LoanMatured = 'LoanMatured',
  LoanApproved = 'LoanApproved',
  LoanRepayment = 'LoanRepayment',
  LoanRolloverInitiated = 'LoanRolloverInitiated',
  LoanRolloverFinalized = 'LoanRolloverFinalized', //Term Email Template
  Withdraw = 'Withdraw',
  RedeemRepay = 'RedeemRepay',
  RedeemAccepted = 'RedeemAccepted',
  RedeemRequested = 'RedeemRequested', //Both Email Template
  RedeemReleased = 'RedeemReleased', //Both Email Template
  RepayToPoolProcessed = 'RepayToPoolProcessed', //Flex Email Template
  RepayLoanToLender = 'RepayLoanToLender', //Both Email Template
  Redeem = 'Redeem', //Term Email Template ????
  WithdrawEarlyRequested = 'WithdrawEarlyRequested', //Term Email Template
  WithdrawEarlyAccepted = 'WithdrawEarlyAccepted', //Term Email Template
  WithdrawEarlyRepay = 'WithdrawEarlyRepay', //Term Email Template
  RequestFeePaid = 'RequestFeePaid',
  WithdrawFeeVault = 'WithdrawFeeVault',
  Initialized = 'Initialized',
  Transfer = 'Transfer',
  WithdrawRequested = 'WithdrawRequested',
  CrossChainTransferMintDestination = 'CrossChainTransferMintDestination',
  CrossChainTransferBurnSource = 'CrossChainTransferBurnSource',
  CrossChainTransferApproveDestination = 'CrossChainTransferApproveDestination',
  CrossChainTransferApproveSource = 'CrossChainTransferApproveSource',
  InterestAccrual = 'InterestAccrual',
}
/* eslint-disable  @typescript-eslint/no-duplicate-enum-values */
export enum EventNameForTemplate {
  Undefined = 'Undefined',
  AccountCredit = 'AccountCredit',
  AccountDebit = 'AccountDebit',
  InterestAccrual = 'Interest Accrual',
  PoolCreated = 'Pool Created',
  LoanCreated = 'Loan Created',
  Deposit = 'Investment',
  PoolDeposit = 'Investment', //Both Email Template, though Flex says Loan Confirmed and Term says Investment Successful
  //  DepositTransfer = 'Deposit Transfer',
  LoanMatured = 'Loan Matured',
  LoanApproved = 'Loan Approved',
  LoanRepayment = 'Loan Repayment',
  LoanRolloverInitiated = 'Loan Rollover Initiated',
  LoanRolloverFinalized = 'Loan Rollover Finalized', //Term Email Template
  Withdraw = 'Loan Repayment',
  RedeemRequested = 'Withdrawal Request', //Both Email Template
  RedeemReleased = 'Withdrawal Released', //Both Email Template
  RepayToPoolProcessed = 'Withdrawal Processed', //Flex Email Template
  RepayLoanToLender = 'Loan Repayment', //Both Email Template
  Redeem = 'Loan Repayment', //Term Email Template ????
  RedeemRepay = 'Loan Repayment', //Term Email Template
  RedeemAccepted = 'Withdrawal Accepted', //Both Email Template
  WithdrawEarlyRequested = 'Early Withdrawal Requested', //Term Email Template
  WithdrawEarlyAccepted = 'Early Withdrawal Accepted', //Term Email Template
  WithdrawEarlyRepay = 'Loan Repayment', //Term Email Template
  RequestFeePaid = 'Request Fee Paid',
  WithdrawFeeVault = 'Withdraw Fee Vault',
  Initialized = 'Initialized',
  Transfer = 'Transfer',
  CrossChainTransferApproveSource = 'Cross Chain Transfer Approve Source',
  CrossChainTransferApproveDestination = 'Cross Chain Transfer Approve Destination',
  CrossChainTransferBurnSource = 'Cross Chain Transfer Burn Source',
  CrossChainTransferMintDestination = 'Cross Chain Transfer Mint Destination',
}
export const WithdrawEventTypes: EventTypeForTemplate[] = [
  EventTypeForTemplate.Withdraw,
  EventTypeForTemplate.WithdrawEarlyRequested,
  EventTypeForTemplate.WithdrawEarlyAccepted,
  EventTypeForTemplate.WithdrawEarlyRepay,
  EventTypeForTemplate.Redeem,
  EventTypeForTemplate.RedeemRequested,
  EventTypeForTemplate.RedeemReleased,
  EventTypeForTemplate.RepayToPoolProcessed,
  EventTypeForTemplate.RepayLoanToLender,
  EventTypeForTemplate.AccountDebit,
  EventTypeForTemplate.RedeemAccepted,
  EventTypeForTemplate.RedeemRepay,
]

export const LenderLedgerEventTypes: EventTypeForTemplate[] = [
  // Deposit events
  EventTypeForTemplate.Deposit,
  EventTypeForTemplate.PoolDeposit,
  EventTypeForTemplate.AccountCredit,

  // Withdrawal events
  ...WithdrawEventTypes,
]

export enum TemplateFormat {
  USDC = 'USDC',
  BPS = 'BPS',
  Date = 'Date',
  Time = 'Time',
  DatePlusDays = 'DatePlusDays',
  DateTime = 'DateTime',
  EthAddr = 'EthAddr',
  APR = 'APR',
}

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const renderTemplate = (template: string, dataObject: TemplateDataObject): string => {
  Handlebars.registerHelper(TemplateFormat.USDC, function (weiString: ERC20Wei) {
    return erc20WeiToCommaDecString(weiString) + ' ' + dataObject?.event?.currency
  })
  Handlebars.registerHelper(TemplateFormat.DateTime, function (ethTimestamp: number) {
    return toLongUTCDateString(ethTimestamp) + ' ' + toUTCTimeString(ethTimestamp)
  })
  Handlebars.registerHelper(TemplateFormat.Time, function (ethTimestamp: number) {
    return toUTCTimeString(ethTimestamp)
  })
  Handlebars.registerHelper(TemplateFormat.Date, function (ethTimestamp: number) {
    return toLongUTCDateString(ethTimestamp)
  })
  Handlebars.registerHelper(TemplateFormat.DatePlusDays, function (ethTimestamp: number, days: number, days2 = 0) {
    return toLongUTCDateString(ethTimestamp + (days + days2) * 24 * 60 * 60)
  })
  Handlebars.registerHelper(TemplateFormat.EthAddr, function (addr: string) {
    if (!addr) {
      return '0x0000000'
    }
    return ethValueDisplay(addr)
  })
  Handlebars.registerHelper(TemplateFormat.APR, function (rate: number) {
    return rate / 100 + ' %'
  })
  Handlebars.registerHelper(TemplateFormat.BPS, function (bps: string) {
    if (!bps) {
      return '0%'
    }
    return aprToDisplay(bps)
  })

  const compiled = Handlebars.compile(template)
  const result = compiled(dataObject)
  return result
}
